import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Toaster } from "@/components/ui/sonner";
import IdleTimeoutDialog from '@/components/IdleTimeoutDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from '@/hooks/useAuth';
import { UpgradeRequired } from '@/components/UpgradeRequired';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient, setupRealtimeInvalidation, registerSocketInvalidation } from '@/services/queryClient';
import { socketService } from '@/services/socketService';
import { useCacheEventsBridge } from '@/hooks/useCacheEventsBridge';
import { useAppPrefetch } from '@/hooks/useAppPrefetch';
import { useOwnerRoleBootstrap } from '@/hooks/useOwnerRoleBootstrap';
import { getAuthTokenWithActivity } from '@/utils/apiUtils';
import { toast } from 'sonner';
import { devLog } from '@/utils/devLog';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { ConsentProvider } from '@/contexts/ConsentContext';
import { CookieConsent, CookiePreferencesButton } from '@/components/CookieConsent';
import { isUpdateAvailable, useVersionStore } from '@/stores/versionStore';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { OwnerLayout } from '@/components/layout/OwnerLayout';
import { OwnerBillingStrip } from '@/components/raises/owner/OwnerBillingStrip';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { resolveLayoutKind } from '@/components/layout/sidebarConfig';
import {
  DOCUMENT_STUDIO,
  type DocumentStudioFeatureContract,
} from '@/config/features';
import { isOwnerAllowedPath } from '@/config/ownerRoutes';
import { RaisesPublicFrame } from '@/components/raises/RaisesPublicFrame';

// Lazy-loaded pages
import React from 'react';
const Login = React.lazy(() => import('@/pages/Login'));
const Register = React.lazy(() => import('@/pages/Register'));
const CompleteProfile = React.lazy(() => import('@/pages/CompleteProfile'));
const Projects = React.lazy(() => import('@/pages/Projects'));
const ProjectDetail = React.lazy(() => import('@/pages/ProjectDetail'));
const RiskMonitorDeal = React.lazy(() => import('@/pages/RiskMonitorDeal'));
const SubmitProjectV2 = React.lazy(() => import('@/pages/SubmitProjectV2'));
const CoInvest = React.lazy(() => import('@/pages/CoInvest'));
const UserProfile = React.lazy(() => import('@/pages/UserProfile'));
const AdminDashboard = React.lazy(() => import('@/pages/AdminDashboard'));
const AdminApiKeysPage = React.lazy(() => import('@/pages/admin/ApiKeys'));
const InvestorDatabase = React.lazy(() => import('@/pages/admin/InvestorDatabase'));
const ActiveRaisesDatabase = React.lazy(() => import('@/pages/admin/ActiveRaisesDatabase'));
const CampaignsDashboard = React.lazy(() => import('@/pages/admin/CampaignsDashboard'));
const ProponentDashboardView = React.lazy(() => import('@/pages/admin/ProponentDashboardView'));
const Index = React.lazy(() => import('./pages/Index'));
const About = React.lazy(() => import('./pages/About'));
const Team = React.lazy(() => import('./pages/Team'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Careers = React.lazy(() => import('./pages/Careers'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies'));
const CaseStudyDetail = React.lazy(() => import('./pages/CaseStudyDetail'));
const Publications = React.lazy(() => import('./pages/Publications'));
const PublicationDetail = React.lazy(() => import('./pages/PublicationDetail'));
const Help = React.lazy(() => import('./pages/Help'));
const Documentation = React.lazy(() => import('./pages/Documentation'));
const Terms = React.lazy(() => import('@/pages/Terms'));
const Privacy = React.lazy(() => import('@/pages/Privacy'));
const Blog = React.lazy(() => import('@/pages/Blog'));
const BookDemo = React.lazy(() => import('@/pages/BookDemo'));
const WorkshopInquiry = React.lazy(() => import('@/pages/WorkshopInquiry'));
const Dataroom = React.lazy(() => import('@/pages/dataroom/Dataroom'));
const VerifyEmail = React.lazy(() => import('@/pages/VerifyEmail'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const ResetPasswordComplete = React.lazy(() => import('./pages/ResetPasswordComplete'));
const AuthHandler = React.lazy(() => import('./pages/AuthHandler'));
const ProjectNotes = React.lazy(() => import('@/pages/ProjectNotes'));
const PendingApproval = React.lazy(() => import('@/pages/PendingApproval'));
const ProjectChat = React.lazy(() => import('@/pages/admin/ProjectChat'));
// EconomicDevAssessment module is now mounted exclusively via ProjectDetail
// (EdaProjectShell) for projects with submission_category='economic_development'.
// The legacy /eda-example sandbox has been retired.
const IntelligencePlatform = React.lazy(() => import('@/pages/IntelligencePlatform'));
const GlobalInfrastructureIntelligence = React.lazy(() => import('@/pages/intelligence/GlobalInfrastructureIntelligence'));
const InvestorIntelligence = React.lazy(() => import('@/pages/intelligence/InvestorIntelligence'));
const Organization = React.lazy(() => import('@/pages/Organization'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const InvestLanding = React.lazy(() => import('@/pages/InvestLanding'));
const SubmitLanding = React.lazy(() => import('@/pages/SubmitLanding'));
const MarketIntelligenceLanding = React.lazy(() => import('@/pages/MarketIntelligenceLanding'));
const AuditableResearchLanding = React.lazy(() => import('@/pages/AuditableResearchLanding'));
const ManagementScanLanding = React.lazy(() => import('@/pages/ManagementScanLanding'));
const StudioLanding = React.lazy(() => import('@/pages/StudioLanding'));

// Public Raises directory & profiles (sub-project ③B). Public/SEO surfaces —
// rendered in PublicLayout for anonymous viewers (and the app shell when signed
// in), NOT behind AdminRoute/ApprovalPendingRoute: the ③A API enforces the tier.
const RaisesLanding = React.lazy(() => import('@/pages/raises/RaisesLanding'));
const RaisesDirectory = React.lazy(() => import('@/pages/raises/RaisesDirectory'));
const RaiseProfile = React.lazy(() => import('@/pages/raises/RaiseProfile'));
const CompanyProfile = React.lazy(() => import('@/pages/raises/CompanyProfile'));

// ⑤B Owner self-service: the claim page (public, token-capable) + the owner
// dashboard (signed-in developers; the route guard redirects anon → login, and
// ⑤A's `checkAuth`/`isOwner` is the real authorization).
const ClaimProject = React.lazy(() => import('@/pages/raises/ClaimProject'));
const ClaimVerifyEmail = React.lazy(() => import('@/pages/raises/ClaimVerifyEmail'));
const ConnectRespond = React.lazy(() => import('@/pages/raises/ConnectRespond'));
const OwnerDashboard = React.lazy(() => import('@/pages/owner/OwnerDashboard'));
const OwnerBilling = React.lazy(() => import('@/pages/owner/OwnerBilling'));


// Authenticated workspace pages
const AuthHome = React.lazy(() => import('@/pages/authenticated/Home'));
const Dms = React.lazy(() => import('@/pages/authenticated/Dms'));
const DealFlow = React.lazy(() => import('@/pages/authenticated/DealFlow'));
const Studio = React.lazy(() => import('@/pages/authenticated/Studio'));
const ArtieAssistant = React.lazy(() => import('@/pages/authenticated/ArtieAssistant'));

// ----------------------
// Scroll To Top Component
// ----------------------

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ----------------------
// Route Change Handler for Seamless Updates
// ----------------------

const RouteChangeHandler = () => {
  const location = useLocation();
  const isInitialMount = useRef(true);
  const previousPathname = useRef<string | null>(null);

  // Start background version checks (polls /version.json every 5 min + on visibility change)
  useEffect(() => {
    const store = useVersionStore.getState();
    store.startBackgroundChecks();
    return () => store.stopBackgroundChecks();
  }, []);

  // Route change → reload if update available
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousPathname.current = location.pathname;
      return;
    }

    if (previousPathname.current === location.pathname) {
      return;
    }

    previousPathname.current = location.pathname;

    if (isUpdateAvailable()) {
      console.info('[RouteChangeHandler] Update available, performing full reload');
      window.location.reload();
    }
  }, [location.pathname]);

  return null;
};

// ----------------------
// Loading Component
// ----------------------

const LoadingSpinner = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="flex items-center justify-center min-h-screen bg-white"
  >
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </motion.div>
);

// ----------------------
// Optimized Route Wrappers
// ----------------------

/**
 * Preserve the exact route matched by the scoped `<Routes location={...}>`.
 *
 * `AnimatedRoutes` intentionally holds an older display location while a route
 * exits. Reading `window.location` observes the newer browser URL during that
 * window, while a once-per-component ref can remain stale when React reconciles
 * two guarded routes under the same animation key. Router location is the source
 * of truth here; memoization keeps `<Navigate>` state referentially stable for one
 * matched route and refreshes it only when pathname/search/hash really change.
 */
function useStableLoginOrigin(): Readonly<{ from: string }> {
  const location = useLocation();
  return useMemo(
    () => ({ from: `${location.pathname}${location.search}${location.hash}` }),
    [location.pathname, location.search, location.hash],
  );
}

const PreserveLocationRedirect = ({
  to,
  preserveSearchAndHash,
}: {
  to: string;
  preserveSearchAndHash: boolean;
}) => {
  const location = useLocation();
  const destination = useMemo(
    () => ({
      pathname: to,
      search: preserveSearchAndHash ? location.search : '',
      hash: preserveSearchAndHash ? location.hash : '',
    }),
    [to, preserveSearchAndHash, location.search, location.hash],
  );
  return <Navigate to={destination} replace />;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const loginOrigin = useStableLoginOrigin();

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!user) return <Navigate to="/login" state={loginOrigin} replace />;
  return <>{children}</>;
};

/**
 * Guard for the owner console surface (`/owner`, `/owner/claims/verify/:token`).
 *
 * Requires only an authenticated session — the scoped `raise_owner` persona AND
 * dual-role accounts (a real developer/admin who also owns a claimed raise) are
 * both legitimately allowed here. Per-claim authorization is enforced server-side
 * by `ownershipService.isOwner` on the `/api/owner/*` endpoints, NOT in this
 * guard. An anonymous visitor is bounced to `/login` with the return path
 * preserved (e.g. so a claim-verify token link survives a sign-in round-trip).
 */
export const OwnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const loginOrigin = useStableLoginOrigin();

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!user) return <Navigate to="/login" state={loginOrigin} replace />;
  return <>{children}</>;
};

export const ApprovalPendingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isApproved, isRaiseOwner } = useAuth();
  const loginOrigin = useStableLoginOrigin();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" state={loginOrigin} replace />;
  }

  // Wall off the scoped owner persona BEFORE the approval check. A `raise_owner`
  // is intentionally never `approved` (`approved:true` is a platform master key),
  // so without this branch they would be bounced to /pending-approval forever.
  // Their only surface is /owner.
  if (isRaiseOwner) {
    return <Navigate to="/owner" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};

export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isAdmin, isRaiseOwner } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Deny-list: an owner manually typing /admin bounces back to their console.
  if (isRaiseOwner) return <Navigate to="/owner" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

export const SubscriptionRoute = ({ children, title, description }: { children: React.ReactNode; title: string; description: string; }) => {
  const { isLoading, user, isSubscriptionLoading, subscription, isRaiseOwner } = useAuth();

  if (isLoading || (!user && isSubscriptionLoading)) {
    return <LoadingSpinner />;
  }
  if (!user) return <Navigate to="/login" replace />;

  // Defense-in-depth: every subscription-gated route is also wrapped in
  // ApprovalPendingRoute (which already redirects an owner to /owner), but a
  // `raise_owner` never holds a paid subscription — guard here too so an owner can
  // never render the UpgradeRequired upsell even if this guard is ever used alone.
  if (isRaiseOwner) return <Navigate to="/owner" replace />;

  if (isSubscriptionLoading) {
    return <LoadingSpinner />;
  }

  if (subscription === 'pilot') {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="w-full">
        <UpgradeRequired title={title} description={description} backHref="/projects" />
      </motion.div>
    );
  }

  return <>{children}</>;
};

// ----------------------
// Home Redirect — auth users go to /home, unauth see landing
// ----------------------

export const HomeRedirect = () => {
  const { user, isLoading, isRaiseOwner } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  // The scoped owner persona lands on their console, never the developer /home.
  if (isRaiseOwner) return <Navigate to="/owner" replace />;
  if (user) return <Navigate to="/home" replace />;
  return <Index />;
};

// ----------------------
// Animated Routes Component
// ----------------------

export const AnimatedRoutes = ({
  documentStudioFeature = DOCUMENT_STUDIO,
}: {
  documentStudioFeature?: Readonly<DocumentStudioFeatureContract>;
} = {}) => {
  const location = useLocation();
  const { user, isLoading, isRaiseOwner } = useAuth();

  // Defer location updates to prevent Suspense fallback flash during navigation.
  // BrowserRouter doesn't wrap navigate() in startTransition (unlike createBrowserRouter),
  // so lazy components re-suspend on every route change, briefly showing LoadingSpinner.
  // By deferring, React keeps the old UI visible while the new route prepares.
  const [, startTransition] = useTransition();
  const [displayLocation, setDisplayLocation] = useState(location);

  useEffect(() => {
    startTransition(() => {
      setDisplayLocation(location);
    });
  }, [location, startTransition]);

  // Group routes that share animation keys to prevent white-flash on navigation
  const isBlogRoute = displayLocation.pathname.startsWith('/blog');
  const isDataroomRoute = displayLocation.pathname.startsWith('/dataroom');
  const isIntelligenceRoute = displayLocation.pathname.startsWith('/intelligence');
  const isCaseStudiesRoute = displayLocation.pathname.startsWith('/case-studies');
  const isPublicationsRoute = displayLocation.pathname.startsWith('/publications');
  const isProjectsRoute = displayLocation.pathname.startsWith('/projects');
  const isWorkspaceRoute = ['/home', DOCUMENT_STUDIO.route, '/dms', '/deal-flow', '/artie-assistant'].some(
    (p) => displayLocation.pathname.startsWith(p),
  );

  let locationKey = displayLocation.pathname;
  if (isBlogRoute) locationKey = '/blog';
  if (isDataroomRoute) locationKey = '/dataroom';
  if (isIntelligenceRoute) locationKey = '/intelligence';
  if (isCaseStudiesRoute) locationKey = '/case-studies';
  if (isPublicationsRoute) locationKey = '/publications';
  if (isProjectsRoute) locationKey = '/projects';
  if (isWorkspaceRoute) locationKey = '/workspace';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [displayLocation.pathname]);

  const routes = (
    <AnimatePresence mode="wait">
      <motion.div
        key={locationKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="w-full h-full"
      >
        <Suspense fallback={<LoadingSpinner />}>
          <Routes location={displayLocation}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/index" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/team" element={<Team />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/publications/:slug" element={<PublicationDetail />} />
            <Route path="/help" element={<Help />} />
            <Route path="/docs" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:blogId" element={<Blog />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/workshop" element={<WorkshopInquiry />} />
            <Route path="/invest" element={<InvestLanding />} />
            <Route path="/submit" element={<SubmitLanding />} />
            <Route path="/market-intelligence" element={<MarketIntelligenceLanding />} />
            <Route path="/auditable-research" element={<AuditableResearchLanding />} />
            <Route path="/management-scan" element={<ManagementScanLanding />} />
            {documentStudioFeature.enabled && <Route path="/studio-landing" element={<StudioLanding />} />}

            {/*
              Public Raises directory & profiles (sub-project ③B, spec §4 routes).
              Plain public routes — NOT wrapped in AdminRoute/ApprovalPendingRoute:
              anonymous visitors must browse + view teasers for SEO, and the ③A
              tiered API is the sole security boundary. The layout split below
              renders these in PublicLayout for anonymous viewers and the
              authenticated app shell when signed in (same page components adapt
              to the tier the API returns, not to the route).
            */}
            <Route path="/raises" element={<RaisesPublicFrame><RaisesLanding /></RaisesPublicFrame>} />
            <Route path="/raises/directory" element={<RaisesPublicFrame><RaisesDirectory /></RaisesPublicFrame>} />
            <Route path="/raises/:slug" element={<RaisesPublicFrame><RaiseProfile /></RaisesPublicFrame>} />
            {/*
              ⑤B claim page — public + token-capable (`?token=`). React Router v6
              ranks by specificity, so `/raises/:slug/claim` outranks `/raises/:slug`
              automatically (no ordering tricks needed). It self-gates a signed-out
              viewer to a sign-in prompt; ⑤A is the real authz.
            */}
            <Route path="/raises/:slug/claim" element={<RaisesPublicFrame><ClaimProject /></RaisesPublicFrame>} />
            {/*
              Connect owner-action landing — PUBLIC + token-authorized (the single-use,
              action-bound token in the path is the authorization; no auth guard). Target
              of the Accept/Decline CTA in a Connect notification email; POSTs to
              /api/connect/respond/:token. Kept outside RaisesPublicFrame so the emailed
              one-click confirm renders on its own minimal page.
            */}
            <Route path="/connect/respond/:token" element={<ConnectRespond />} />
            <Route path="/companies/:slug" element={<RaisesPublicFrame><CompanyProfile /></RaisesPublicFrame>} />

            {/*
              ⑤B owner dashboard — the scoped `raise_owner` console (also reachable
              by dual-role developers/admins who own a raise). OwnerRoute redirects
              an anonymous viewer to /login (preserving the return path); the ⑤A
              `/api/owner` endpoints + `ownershipService.isOwner` enforce the real
              per-claim authorization (a non-owner gets an empty or 403 result).
            */}
            <Route
              path="/owner"
              element={
                <OwnerRoute>
                  <OwnerDashboard />
                </OwnerRoute>
              }
            />

            {/*
              ⑤B M2 owner billing page — the scoped subscription/receipt surface. Its
              path MUST also be in `OWNER_ALLOWED_ROUTES` or the deny-by-default owner
              wall (the layout split below) bounces it to /owner before it can load.
            */}
            <Route
              path="/owner/billing"
              element={
                <OwnerRoute>
                  <OwnerBilling />
                </OwnerRoute>
              }
            />

            {/*
              ⑤ claim email-verification landing — target of the single-use link
              emailed when a work-email domain match routes a claim to the
              `email_verify` round-trip. OwnerRoute redirects an anonymous
              visitor to /login and returns them here (token preserved); the page
              then POSTs the token and the server completes the claim only for the
              uid that initiated it (confused-deputy guard → 403).
            */}
            <Route
              path="/owner/claims/verify/:token"
              element={
                <OwnerRoute>
                  <ClaimVerifyEmail />
                </OwnerRoute>
              }
            />

            <Route path="/intelligence-platform" element={<IntelligencePlatform />} />
            <Route
              path="/intelligence/global-infrastructure/*"
              element={
                <ApprovalPendingRoute>
                  <SubscriptionRoute
                    title="Intelligence Platform Access Required"
                    description="Please upgrade your subscription to access Global Infrastructure Intelligence."
                  >
                    <GlobalInfrastructureIntelligence />
                  </SubscriptionRoute>
                </ApprovalPendingRoute>
              }
            />
            <Route path="/intelligence/investor" element={<InvestorIntelligence />} />
            <Route
              path="/projects"
              element={
                <ApprovalPendingRoute>
                  <Projects />
                </ApprovalPendingRoute>
              }
            />
            {/*
              Per-deal Risk Monitor detail page. React Router v6 ranks the
              static `risk-monitor` segment above the sibling `/:projectId/*`
              splat by specificity (same mechanism `/raises/:slug/claim` relies
              on — no ordering tricks needed), so this matches first.
            */}
            <Route
              path="/projects/:projectId/risk-monitor/:section?"
              element={
                <ApprovalPendingRoute>
                  <RiskMonitorDeal />
                </ApprovalPendingRoute>
              }
            />
            <Route
              path="/projects/:projectId/*"
              element={
                <ApprovalPendingRoute>
                  <ProjectDetail />
                </ApprovalPendingRoute>
              }
            />
          <Route
            path="/co-invest"
            element={
              <ApprovalPendingRoute>
                <CoInvest />
              </ApprovalPendingRoute>
            }
          />
          <Route
            path="/submit-project"
            element={
              <ApprovalPendingRoute>
                <SubmitProjectV2 />
              </ApprovalPendingRoute>
            }
          />
          <Route
            path="/project-notes"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Upgrade Required" description="Please upgrade your subscription to access Notes.">
                  <ProjectNotes />
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          <Route path="/dataroom" element={<Navigate to="/dms" replace />} />
          <Route
            path="/dataroom/:projectId"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Upgrade Required" description="Please upgrade your subscription to access the Data Room.">
                  {/*
                    Route-scoped ErrorBoundary so a render-time crash inside
                    the dataroom surface (e.g. a malformed bootstrap response,
                    an exception inside a tree-view callback) doesn't bubble
                    up to the page-level boundary and white-screen the whole
                    AnimatedRoutes block. Reset keyed by `projectId` so
                    navigating to a different dataroom clears the error
                    state automatically.
                  */}
                  <ErrorBoundary
                    level="page"
                    resetKeys={[window.location.pathname]}
                  >
                    <Dataroom />
                  </ErrorBoundary>
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          {/* Authenticated workspace routes */}
          <Route
            path="/home"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Document Studio" description="Please upgrade to access Document Studio.">
                  <AuthHome />
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          <Route
            path="/dms"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Document Management System" description="Please upgrade to access Document Management System.">
                  <Dms />
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          <Route path="/knowledge" element={<Navigate to="/dms" replace />} />
          <Route
            path="/deal-flow"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Deal Flow" description="Please upgrade to access Deal Flow.">
                  <DealFlow />
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          {documentStudioFeature.enabled && (
            <Route
              path="/studio/:slug?"
              element={
                <ApprovalPendingRoute>
                  <SubscriptionRoute title="Document Studio" description="Please upgrade to access Document Studio.">
                    <Studio />
                  </SubscriptionRoute>
                </ApprovalPendingRoute>
              }
            />
          )}
          {/* Redirect old /my-documents to Document Studio (or home while the studio is disabled) */}
          <Route path="/my-documents" element={<PreserveLocationRedirect
            to={documentStudioFeature.documentsRedirect}
            preserveSearchAndHash={documentStudioFeature.enabled}
          />}
          />
          <Route
            path="/artie-assistant"
            element={
              <ApprovalPendingRoute>
                <SubscriptionRoute title="Artie AI Assistant" description="Please upgrade to access Artie AI Assistant.">
                  <ArtieAssistant />
                </SubscriptionRoute>
              </ApprovalPendingRoute>
            }
          />
          {/* Backward compat: old path follows the same disabled-state contract as /my-documents. */}
          <Route path="/document-studio" element={<PreserveLocationRedirect
            to={documentStudioFeature.legacyRedirect}
            preserveSearchAndHash={documentStudioFeature.enabled}
          />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password-complete" element={<ResetPasswordComplete />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/auth/action" element={<AuthHandler />} />
          <Route
            path="/pending-approval"
            element={
              <ProtectedRoute>
                <PendingApproval />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization"
            element={
              <ProtectedRoute>
                <Organization />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/project-chat"
            element={
              <AdminRoute>
                <ProjectChat />
              </AdminRoute>
            }
          />
          {/* Phase 6.11 — admin-managed per-org API keys. */}
          <Route
            path="/admin/investors"
            element={
              <AdminRoute>
                <InvestorDatabase />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/active-raises"
            element={
              <AdminRoute>
                <ActiveRaisesDatabase />
              </AdminRoute>
            }
          />
          {/* Campaigns — standalone admin page (sidebar ADMIN → Campaigns); the legacy claim-invite console is its
              `?section=claims` view and `/admin?tab=campaigns` deep links redirect here (AdminDashboard). */}
          <Route
            path="/admin/campaigns"
            element={
              <AdminRoute>
                <CampaignsDashboard />
              </AdminRoute>
            }
          />
          {/* Admin read-only view of a proponent's owner console (server audits + authz). */}
          <Route
            path="/admin/proponents/:ownerUid"
            element={
              <AdminRoute>
                <ProponentDashboardView />
              </AdminRoute>
            }
          />
          {/* Retired: Raises Ops is now the consolidated "Raises" tab on /admin.
              Redirect old links/bookmarks; /admin enforces the admin guard. */}
          <Route path="/admin/raises-ops" element={<Navigate to="/admin?tab=raises" replace />} />
          <Route path="/admin/raises-ops/*" element={<Navigate to="/admin?tab=raises" replace />} />
          <Route
            path="/admin/api-keys"
            element={
              <AdminRoute>
                <AdminApiKeysPage />
              </AdminRoute>
            }
          />
          <Route path="/admin-dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/investor-dashboard" element={<Login />} />
          <Route path="/owner-dashboard" element={<Login />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </motion.div>
    </AnimatePresence>
  );

  // Routes that must always render without the authenticated sidebar,
  // regardless of whether a Firebase user object still exists (e.g., during
  // logout when Firebase persistence hasn't fully cleared yet).
  const PUBLIC_ONLY_ROUTES = [
    '/login',
    '/register',
    '/verify-email',
    '/reset-password',
    '/reset-password-complete',
    '/complete-profile',
    '/auth/action',
  ];

  const isPublicOnlyRoute = PUBLIC_ONLY_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(route + '/'),
  );

  // While auth is resolving, show a neutral shell to avoid layout flash
  // (but skip the loading state for public-only routes — they don't need auth)
  if (isLoading && !isPublicOnlyRoute) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Layout split: public-only routes always get PublicLayout (prevents sidebar
  // showing on /login during logout race conditions, and preserves the signed-out
  // /raises/:slug/claim anonymous flow). A signed-in `raise_owner` gets the
  // minimal OwnerLayout (no developer sidebar — the chrome-level deny wall); every
  // other authenticated user keeps the full sidebar layout.
  const layoutKind = resolveLayoutKind({
    isAuthenticated: !!user,
    isRaiseOwner,
    isPublicOnlyRoute,
  });

  if (layoutKind === 'owner') {
    // Deny-by-default wall (root cause of the Phase-1 leak): a scoped
    // `raise_owner` reaching this branch is, by `resolveLayoutKind`'s contract,
    // already authenticated and NOT on a public-only route. Allow ONLY the
    // owner-allowlisted surfaces (`isOwnerAllowedPath`) and bounce everything
    // else — guarded OR unguarded — to /owner. This catches the routes the
    // per-guard redirects miss (`/organization`, `/docs`,
    // `/intelligence-platform`, `/intelligence/investor`, `/pending-approval`,
    // and any future unguarded route). The allowed routes (/owner, /profile, the
    // public Raises routes) match `isOwnerAllowedPath`, so there is no redirect
    // loop. Uses `location.pathname` (the URL being navigated to), so blocked
    // content never renders even for a tick under the deferred `displayLocation`.
    if (!isOwnerAllowedPath(location.pathname)) {
      return <Navigate to="/owner" replace />;
    }
    return (
      <OwnerLayout ownerBanner={<OwnerBillingStrip />}>{routes}</OwnerLayout>
    );
  }
  if (layoutKind === 'authenticated') {
    return <AuthenticatedLayout>{routes}</AuthenticatedLayout>;
  }
  return <PublicLayout>{routes}</PublicLayout>;
};

// Hooks that require QueryClientProvider context — rendered inside the provider tree
// ----------------------
// Global Account Status Handler
// Subscribes to AuthManager's custom DOM events for approval-state transitions.
// Placed inside the Router so useNavigate is available.
// Register.tsx already handles arterra:account-approved on the /register screen,
// but this global handler ensures all three events are caught on any page.
// ----------------------

function GlobalAccountStatusHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleApproved = () => {
      devLog('GlobalAccountStatusHandler: arterra:account-approved received');
      // Only treat as a fresh approval (toast + redirect) if the user is on a
      // pre-approval screen. The Firestore approval listener fires its initial
      // snapshot with status='approved' for every already-approved user, which
      // would otherwise yank approved users away from whatever page they were
      // viewing 1.5s after auth resolves. Restrict the redirect to the pages
      // that actually expect to react to a fresh approval.
      const path = window.location.pathname;
      const isPreApprovalScreen =
        path === '/register' ||
        path === '/pending-approval' ||
        path === '/complete-profile';
      if (!isPreApprovalScreen) {
        devLog('GlobalAccountStatusHandler: ignoring approval event — user not on a pre-approval screen (path=' + path + ')');
        return;
      }
      toast.success('Your account has been approved! Redirecting to the platform...');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    };

    const handleRejected = () => {
      devLog('GlobalAccountStatusHandler: arterra:account-rejected received');
      toast.error('Your account has been rejected. Please contact support.', {
        duration: 8000,
      });
      // AuthManager has already called signOut() before dispatching this event.
      // Redirect to /login so the user sees a clean state.
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    };

    const handlePendingDeletion = () => {
      devLog('GlobalAccountStatusHandler: arterra:account-pending-deletion received');
      toast.warning('Your account is scheduled for deletion. Contact support within 30 days to cancel.', {
        duration: 12000,
      });
    };

    window.addEventListener('arterra:account-approved', handleApproved);
    window.addEventListener('arterra:account-rejected', handleRejected);
    window.addEventListener('arterra:account-pending-deletion', handlePendingDeletion);

    return () => {
      window.removeEventListener('arterra:account-approved', handleApproved);
      window.removeEventListener('arterra:account-rejected', handleRejected);
      window.removeEventListener('arterra:account-pending-deletion', handlePendingDeletion);
    };
  }, [navigate]);

  return null;
}

function AppEffects() {
  useAppPrefetch();
  // Self-heal a stuck raise-owner claimant on any authenticated bootstrap (idempotent
  // reconcile-role; no-op for non-claimants). See useOwnerRoleBootstrap for the gate
  // + loop-safety contract.
  useOwnerRoleBootstrap();
  return null;
}

// ----------------------
// Main App Component
// ----------------------

function App() {
  const { user } = useAuth();

  // Initialize cacheEvents → queryClient bridge
  useCacheEventsBridge();

  // NOTE: useAppPrefetch() is called inside AppEffects (below QueryClientProvider)

  // Initialize real-time invalidation and socket connection
  useEffect(() => {
    let isSubscribed = true;

    const invalidationCleanup = setupRealtimeInvalidation();

    // Phase 3 task 3.7: declarative SOCKET_EVENT_INVALIDATION_MAP wiring.
    // Runs alongside the legacy `setupRealtimeInvalidation` during the
    // migration so no handler is lost. Events present in BOTH are
    // intentionally duplicated — invalidation is idempotent, React
    // Query dedupes in-flight refetches, and the declarative map is
    // the new canonical path going forward.
    const declarativeCleanup = registerSocketInvalidation(queryClient, socketService);

    if (user) {
      import('./services/CrossTabSync').then(({ default: CrossTabSync }) => {
        if (!isSubscribed) return;

        const crossTabSync = CrossTabSync.getInstance();

        if (crossTabSync.getIsLeader() && !socketService.connected) {
          devLog('App: User authenticated and leader tab, connecting socket...');
          getAuthTokenWithActivity(false).then(token => {
            if (isSubscribed && token) {
              socketService.connect(token);
            }
          }).catch(error => {
            devLog('App: Failed to get auth token:', error);
          });
        } else if (!crossTabSync.getIsLeader()) {
          devLog('App: User authenticated but not leader tab, skipping socket connection');
        }
      });
    } else {
      if (socketService.connected) {
        socketService.disconnect();
      }
    }

    return () => {
      isSubscribed = false;
      if (invalidationCleanup) {
        invalidationCleanup();
      }
      declarativeCleanup();
    };
  }, [user]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppEffects />
      <HelmetProvider>
        <TooltipProvider>
          <ConsentProvider>
            <Router>
              <AnalyticsProvider>
                <ErrorBoundary level="app">
                  <div className="flex flex-col w-full h-full">
                    <ScrollToTop />
                    <RouteChangeHandler />
                    <GlobalAccountStatusHandler />
                    <ErrorBoundary level="page" resetKeys={[window.location.pathname]}>
                      <AnimatedRoutes />
                    </ErrorBoundary>
                    <Toaster />
                    <IdleTimeoutDialog />
                    <ConfirmDialog />
                    <CookieConsent />
                    <CookiePreferencesButton />
                  </div>
                </ErrorBoundary>
              </AnalyticsProvider>
            </Router>
          </ConsentProvider>
        </TooltipProvider>
      </HelmetProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
